import React, { useEffect, useState } from 'react'
import { Logo, type LogoProps } from './Logo'
import { useTenantLogoUrl } from '../hooks/useTenantLogoUrl'
import bizFirstLogo from '../../../bizfirst-common-themes/images/BizFirstAiLogo.png'

export interface TenantLogoProps extends Omit<LogoProps, 'src' | 'onError'> {
  /**
   * Backend API base URL the tenant logo asset is fetched from. Optional —
   * defaults to `VITE_API_BASE_URL` (same env var every app already sets),
   * then `window.location.origin`, mirroring `@passport/store`'s own
   * `getApiBaseUrl()` fallback order, so most apps can drop this entirely.
   */
  apiBaseUrl?: string
  /**
   * Shown instead of the tenant's own logo when the tenant hasn't
   * configured one — most commonly a trial-mode tenant with custom
   * branding disabled, or a fetch/decoding failure. Defaults to the
   * bundled BizFirst AI mark.
   */
  fallbackSrc?: string
}

function defaultApiBaseUrl(): string {
  if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_BASE_URL) {
    return (import.meta as any).env.VITE_API_BASE_URL
  }
  if (typeof window !== 'undefined') {
    return window.location.origin
  }
  return 'http://localhost:10001'
}

/**
 * Tenant-aware logo — the one control every app should use to show "the
 * current tenant's logo" instead of each app hand-rolling its own
 * `useTenantLogoUrl` + `<Logo>` wiring.
 *
 * The backend's own asset endpoint (see `useTenantLogoUrl`'s doc comment)
 * already falls back tenant-specific -> platform-default -> an embedded
 * placeholder and never 404s — but that placeholder is a generic
 * broken-looking image, not the real BizFirst mark. This component adds a
 * second, client-side fallback layer specifically for "there is no real
 * logo to show" (a tenant that never uploaded one, or a trial-mode tenant
 * where custom branding is disabled entirely): if the resolved image fails
 * to load, it swaps to the bundled BizFirst AI logo instead of showing a
 * broken-image icon or an ugly placeholder.
 */
export function TenantLogo({ apiBaseUrl, fallbackSrc = bizFirstLogo, alt = 'Logo', ...rest }: TenantLogoProps): React.ReactElement {
  const resolvedApiBaseUrl = apiBaseUrl ?? defaultApiBaseUrl()
  const tenantLogoUrl = useTenantLogoUrl(resolvedApiBaseUrl)
  const [useFallback, setUseFallback] = useState(false)

  // A new tenant URL (e.g. after switching hosts) deserves a fresh attempt —
  // don't stay stuck on the fallback from a previous, unrelated failure.
  useEffect(() => {
    setUseFallback(false)
  }, [tenantLogoUrl])

  return <Logo src={useFallback ? fallbackSrc : tenantLogoUrl} alt={alt} onError={() => setUseFallback(true)} {...rest} />
}

export default TenantLogo
